import React, { useEffect, useState } from 'react';
import { Button, Form } from 'semantic-ui-react';
import './style.less';
import { resetPassword } from '../../../services/Authentication';
import sendToast from '../../../utils/Toast';

const PasswordTab = () => {
    const [loading, setLoading] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [error, setError] = useState('');
    const [isChangePasswordDisabled, setIsChangePasswordDisabled] = useState(true);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const eyeIcon = (visible: boolean, onClick: () => void) => (
        <i
            className="icon pw-eye-icon"
            onClick={onClick}
            style={{ cursor: 'pointer', pointerEvents: 'all' }}
        >
            {visible ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
            ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            )}
        </i>
    );

    // Cognito default password policy: min length 8 + uppercase, lowercase, number, special char
    const getPasswordPolicyChecks = (password: string) => [
        password.length >= 8,
        /[a-z]/.test(password),
        /[A-Z]/.test(password),
        /[0-9]/.test(password),
        /[^A-Za-z0-9]/.test(password),
    ];

    const getPasswordStrength = (password: string) => {
        const score = getPasswordPolicyChecks(password).filter(Boolean).length;

        if (score <= 2) return { percent: 25, color: '#e05252', label: 'Weak' };
        if (score === 3) return { percent: 50, color: '#e0a052', label: 'Fair' };
        if (score === 4) return { percent: 75, color: '#e0c552', label: 'Good' };
        return { percent: 100, color: '#4caf50', label: 'Strong' };
    };

    const newPasswordMeetsPolicy = getPasswordPolicyChecks(newPassword).every(Boolean);
    const passwordsMismatch = Boolean(confirmNewPassword) && newPassword !== confirmNewPassword;

    useEffect(() => {
        setIsChangePasswordDisabled(
            !oldPassword ||
            oldPassword.length < 8 ||
            !newPassword ||
            !newPasswordMeetsPolicy ||
            !confirmNewPassword ||
            passwordsMismatch,
        );
        if (passwordsMismatch) {
            setError('Passwords do not match');
        } else if (newPassword && !newPasswordMeetsPolicy) {
            setError('Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character');
        } else {
            setError('');
        }
    }, [oldPassword, newPassword, confirmNewPassword, newPasswordMeetsPolicy, passwordsMismatch]);

    const updatePassword = async () => {
        setError('');
        setLoading(true);
        try {
            const result = await resetPassword(oldPassword, newPassword);
            if (result) {
                sendToast('success', 'Password reset successfully!');
                setLoading(false);
                setOldPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
            } else {
                setLoading(false);
                setError('Old Password is incorrect');
            }
        } catch (e) {
            setLoading(false);
            setError(e instanceof Error ? e.message : 'Something went wrong');
        }
    };

    return (
        <div className="sect">
            <div className="sect-header">
                <div className="sect-icon password">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                </div>
                <div className="sect-title">Change Password</div>
            </div>
            <div className="sect-body">
                <Form loading={loading} onSubmit={(e) => e.preventDefault()}>
                    <div className="pw-card">
                        <div className="field-group">
                            <div className="field">
                                <Form.Input
                                    value={oldPassword}
                                    type={showOldPassword ? 'text' : 'password'}
                                    placeholder="Old Password"
                                    label="Old Password"
                                    required
                                    icon={eyeIcon(showOldPassword, () => setShowOldPassword((v) => !v))}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                />
                            </div>
                            <div className="field">
                                <Form.Input
                                    value={newPassword}
                                    type={showNewPassword ? 'text' : 'password'}
                                    placeholder="New Password"
                                    label="New Password"
                                    required
                                    icon={eyeIcon(showNewPassword, () => setShowNewPassword((v) => !v))}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                {newPassword && (
                                    <div style={{ marginTop: '8px' }}>
                                        <div className="pw-strength">
                                            <div
                                                className="pw-strength-bar"
                                                style={{
                                                    width: `${getPasswordStrength(newPassword).percent}%`,
                                                    background: getPasswordStrength(newPassword).color,
                                                }}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                marginTop: '4px',
                                                color: getPasswordStrength(newPassword).color,
                                            }}
                                        >
                                            {getPasswordStrength(newPassword).label}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="field">
                                <Form.Input
                                    value={confirmNewPassword}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Confirm New Password"
                                    label="Confirm New Password"
                                    required
                                    error={passwordsMismatch}
                                    icon={eyeIcon(showConfirmPassword, () => setShowConfirmPassword((v) => !v))}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                />
                            </div>

                            {error && <div className="pw-error">{error}</div>}
                        </div>
                    </div>

                    <div className="save-row" style={{ marginTop: '20px' }}>
                        <Button
                            className="btn-save"
                            type="button"
                            onClick={updatePassword}
                            disabled={isChangePasswordDisabled}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                            Update Password
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default PasswordTab;
